import { fieldLogFootageStatusFilterSchema } from '@/features/field-log/lib/footageReview';
import { isOperatorPresence } from '@/features/field-log/lib/sessionFilters';
import type {
  FieldLogHistoryEmptyState,
  FieldLogHistorySearch,
} from '@/features/field-log/types/fieldLogList';
import { hasAscendingUtcTimestampRange, utcIsoTimestampSchema } from '@/shared/lib/dateTime';
import { z } from 'zod';

/** Keeps externally edited History URLs from reaching the API as invalid requests. */
export const fieldLogHistorySearchSchema = z
  .object({
    startedAtFrom: utcIsoTimestampSchema.optional().catch(undefined),
    startedAtTo: utcIsoTimestampSchema.optional().catch(undefined),
    venueName: z.string().optional().catch(undefined),
    cameraOpsCode: z.array(z.string()).optional().catch(undefined),
    hasSessions: z.boolean().optional().catch(undefined),
    footageStatus: fieldLogFootageStatusFilterSchema,
    operatorPresence: z.string().optional().catch(undefined),
    recording: z.boolean().optional().catch(undefined),
    organizationId: z.array(z.string()).optional().catch(undefined),
  })
  .transform((search): FieldLogHistorySearch => {
    const dateRange = hasAscendingUtcTimestampRange(search.startedAtFrom, search.startedAtTo)
      ? { startedAtFrom: search.startedAtFrom, startedAtTo: search.startedAtTo }
      : {};
    return {
      venueName: search.venueName,
      cameraOpsCode: search.cameraOpsCode,
      hasSessions: search.hasSessions,
      footageStatus: search.footageStatus,
      operatorPresence:
        search.operatorPresence && isOperatorPresence(search.operatorPresence)
          ? search.operatorPresence
          : undefined,
      recording: search.recording,
      organizationId: search.organizationId,
      ...dateRange,
    };
  });

/** Normalizes URL-owned filters into the History empty-state discriminant. */
export function fieldLogHistoryEmptyState(
  search: FieldLogHistorySearch,
): FieldLogHistoryEmptyState {
  return search.startedAtFrom === undefined &&
    search.venueName === undefined &&
    search.hasSessions === undefined &&
    search.operatorPresence === undefined &&
    search.recording === undefined &&
    search.organizationId === undefined &&
    !search.footageStatus?.length &&
    !search.cameraOpsCode?.length
    ? 'unfiltered'
    : 'filtered';
}
